/**
 * Reportes → Placas por cliente (`/reportes/placas-clientes`).
 *
 * Puerto de `Reportes::reporte_placas_clientes` + `Cliente_model::get_all_placas`
 * + `views/menu/reportes/reporte_placas_clientes.php`: era un buscador («escribe la
 * placa y te dice el cliente») que resolvía en el navegador sobre el catálogo
 * completo y respondía con un aviso; aquí se escribe la placa (o el documento, o
 * el nombre del cliente) y las coincidencias se ven en una tabla, con el cliente,
 * su documento, el tipo de placa y el origen.
 *
 * Reglas conservadas: sólo se listan las placas **vigentes** (`placa.estado_placa = 1`
 * y `customer_vehicles.status = 1`) y la búsqueda es por placa o por cliente.
 *
 * Diferencias anotadas:
 * - El panel anterior elegía **una** fuente según el modo del sistema
 *   (`use_system`: la tabla `placa` en WEB y los vehículos del APP en
 *   `customer_vehicles`); aquí se listan **las dos**, cada una con su origen, que es
 *   lo que hace falta para que el buscador encuentre una placa registrada en
 *   cualquiera de los dos sitios.
 * - Se añade el reparto por cliente (quién tiene varias placas registradas).
 * - El buscador no tiene exportación: es una consulta puntual, no un listado por
 *   periodo (`verificar` en el panel anterior tampoco la tenía).
 */

import { ArrowLeft, Info, RefreshCw, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { ApiError } from '@/lib/http/api-error';
import { formatNumber } from '@/lib/utils';

import {
  useCatalogosPlacas,
  usePlacasClientes,
  type FiltrosPlacas,
} from '../reportes.api';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

export function ReportePlacasClientesPage() {
  useDocumentTitle('Reportes · Placas por cliente');

  const [filtros, setFiltros] = useState<FiltrosPlacas>({ busqueda: '', origen: '' });

  const catalogos = useCatalogosPlacas();
  const consulta = usePlacasClientes(filtros);

  const datos = consulta.data;
  const resumen = datos?.resumen;
  const placas = datos?.placas ?? [];
  // El listado se pagina en el navegador; los contadores y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(placas, { clave: filtros });
  const conVarias = datos?.por_cliente ?? [];

  const buscar = (busqueda: string) => setFiltros((previos) => ({ ...previos, busqueda }));

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Placas por cliente</h1>
          <p className="mt-1 text-sm text-ink-500">
            A qué cliente pertenece una placa registrada, en el panel o en el APP.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">Buscar</span>
              <span className="relative">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  autoFocus
                  value={filtros.busqueda}
                  onChange={(evento) => buscar(evento.target.value)}
                  placeholder="Placa, documento o nombre del cliente…"
                  className={`${CONTROL} pl-9`}
                  aria-label="Buscar placa"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-ink-700">Origen</span>
              <Select
                value={filtros.origen}
                onChange={({ target }) => setFiltros((previos) => ({ ...previos, origen: target.value }))}
                className={CONTROL}
                aria-label="Origen de la placa"
              >
                <option value="">Todas ({formatNumber(
                  (catalogos.data?.resumen.web ?? 0) + (catalogos.data?.resumen.app ?? 0),
                )})</option>
                {(catalogos.data?.origenes ?? []).map((origen) => (
                  <option key={origen.valor} value={origen.valor}>
                    {origen.etiqueta} (
                    {formatNumber(
                      origen.valor === 'WEB'
                        ? catalogos.data?.resumen.web ?? 0
                        : catalogos.data?.resumen.app ?? 0,
                    )}
                    )
                  </option>
                ))}
              </Select>
            </label>
          </div>

          {(catalogos.data?.tipos.length ?? 0) > 0 ? (
            <p className="border-t border-ink-100 pt-3 text-xs text-ink-500">
              Tipos registrados en el panel:{' '}
              {(catalogos.data?.tipos ?? [])
                .map((tipo) => `${tipo.tipo} (${formatNumber(tipo.placas)})`)
                .join(' · ')}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudieron cargar las placas">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Placas',
            valor: formatNumber(resumen?.placas ?? 0),
            detalle: 'coincidencias con la búsqueda',
          },
          {
            etiqueta: 'Clientes',
            valor: formatNumber(resumen?.clientes ?? 0),
            detalle: `${formatNumber(resumen?.con_varias ?? 0)} con más de una placa`,
          },
          {
            etiqueta: 'Del panel',
            valor: formatNumber(resumen?.web ?? 0),
            detalle: 'tabla `placa` (modo WEB)',
          },
          {
            etiqueta: 'Del APP',
            valor: formatNumber(resumen?.app ?? 0),
            detalle: 'tabla `customer_vehicles`',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Placas registradas ({formatNumber(placas.length)})
            </h2>
            {filtros.busqueda ? (
              <span className="text-xs text-ink-500">
                búsqueda «{filtros.busqueda}»
              </span>
            ) : null}
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-y-auto rounded-card border border-ink-100">
              <table className="w-full table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[9rem] px-1.5 py-2">Placa</th>
                    <th className="px-1.5 py-2">Cliente</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Documento</th>
                    <th className="w-[7rem] px-1.5 py-2">Tipo</th>
                    <th className="w-[5rem] px-1.5 py-2">Origen</th>
                  </tr>
                </thead>
                <tbody>
                  {placas.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={5} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay placas que coincidan con la búsqueda.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila, indice) => (
                    <tr
                      key={`${fila.origen}-${fila.placa}-${indice}`}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">{fila.placa}</p>
                        {fila.descripcion ? (
                          <p className="truncate text-[11px] text-ink-500">{fila.descripcion}</p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '(sin cliente)'}
                        </p>
                      </td>
                      <td className="px-1.5 py-2 text-ink-600">{fila.documento ?? '—'}</td>
                      <td className="px-1.5 py-2 text-ink-600">{fila.tipo || '—'}</td>
                      <td className="px-1.5 py-2">
                        <Badge variant={fila.origen === 'APP' ? 'success' : 'outline'}>
                          {fila.origen === 'APP' ? 'APP' : 'Panel'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="placas" />
            </>
          ) : null}
        </CardContent>
      </Card>

      {conVarias.length > 0 ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">
              Clientes con varias placas ({formatNumber(conVarias.length)})
            </h2>

            <div className="overflow-x-auto rounded-card border border-ink-100">
              <table className="w-full min-w-[32rem] border-collapse text-[13px]">
                <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Cliente</th>
                    <th className="w-[7.5rem] px-2 py-2">Documento</th>
                    <th className="w-[4.5rem] px-2 py-2 text-right">Placas</th>
                    <th className="px-2 py-2">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {conVarias.map((fila) => (
                    <tr key={fila.cliente_id} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{fila.cliente}</td>
                      <td className="px-2 py-2 text-ink-600">{fila.documento ?? '—'}</td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatNumber(fila.placas)}
                      </td>
                      <td className="px-2 py-2 text-ink-600">{fila.detalle.join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Se listan las placas vigentes de las dos fuentes (la tabla del panel y los vehículos del
        APP): en esta copia hay placas registradas en las dos, así que la misma placa puede salir
        dos veces, una por origen. La pantalla anterior buscaba en una sola fuente y respondía con
        un aviso; aquí la búsqueda es por placa, documento o nombre del cliente, y el buscador no
        necesita exportación porque es una consulta puntual.
      </p>
    </div>
  );
}
